import { useState, useEffect, useRef, useCallback } from 'react'

const THROTTLE_MS = 30_000 // only reset timers once per 30s of continuous activity

export function useIdleTimeout({ timeoutMs, warningMs = 60_000, onSignOut, enabled = true, storageKey }) {
  const [showWarning, setShowWarning]   = useState(false)
  const [secondsLeft, setSecondsLeft]   = useState(Math.floor(warningMs / 1000))

  const warnTimerRef     = useRef(null)
  const signOutTimerRef  = useRef(null)
  const countdownRef     = useRef(null)
  const lastResetRef     = useRef(0)
  const warningActiveRef = useRef(false)

  const clearAll = useCallback(() => {
    clearTimeout(warnTimerRef.current)
    clearTimeout(signOutTimerRef.current)
    clearInterval(countdownRef.current)
  }, [])

  const persistActivity = useCallback(() => {
    if (storageKey) localStorage.setItem(storageKey, String(Date.now()))
  }, [storageKey])

  const startTimers = useCallback(() => {
    warningActiveRef.current = false
    clearAll()
    setShowWarning(false)

    warnTimerRef.current = setTimeout(() => {
      warningActiveRef.current = true
      setShowWarning(true)
      setSecondsLeft(Math.floor(warningMs / 1000))
      countdownRef.current = setInterval(() => {
        setSecondsLeft(s => Math.max(0, s - 1))
      }, 1000)
    }, timeoutMs - warningMs)

    signOutTimerRef.current = setTimeout(() => {
      clearAll()
      setShowWarning(false)
      if (storageKey) localStorage.removeItem(storageKey)
      onSignOut()
    }, timeoutMs)
  }, [timeoutMs, warningMs, onSignOut, clearAll, storageKey])

  const stayLoggedIn = useCallback(() => {
    lastResetRef.current = Date.now()
    persistActivity()
    startTimers()
  }, [startTimers, persistActivity])

  useEffect(() => {
    if (!enabled) {
      clearAll()
      setShowWarning(false)
      return
    }

    // On mount (or re-enable), check how long the app was closed.
    // If more time has passed than the timeout, sign out immediately.
    if (storageKey) {
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        const elapsed = Date.now() - Number(stored)
        if (elapsed > timeoutMs) {
          localStorage.removeItem(storageKey)
          onSignOut()
          return
        }
      }
    }

    const onActivity = () => {
      if (warningActiveRef.current) return
      const now = Date.now()
      if (now - lastResetRef.current < THROTTLE_MS) return
      lastResetRef.current = now
      persistActivity()
      startTimers()
    }

    lastResetRef.current = Date.now()
    persistActivity()
    startTimers()

    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll']
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }))
    return () => {
      clearAll()
      events.forEach(e => window.removeEventListener(e, onActivity))
    }
  }, [enabled, startTimers, clearAll, storageKey, timeoutMs, onSignOut, persistActivity])

  return { showWarning, secondsLeft, stayLoggedIn }
}
